class ProductGallery extends HTMLElement {
  constructor() {
    super();

    this.onThumbnailClick = this.onThumbnailClick.bind(this);
    this.setActiveThumbnail = this.setActiveThumbnail.bind(this);
  }

  connectedCallback() {
    this.sliderRoot = this.querySelector('.product-gallery__slider');
    this.thumbnails = Array.from(this.querySelectorAll('[data-product-gallery-thumbnail]'));

    if (!this.sliderRoot || typeof window.Splide !== 'function') return;

    const initialSlide = Number.parseInt(this.dataset.initialSlide, 10) || 0;
    const hasMultipleMedia = this.querySelectorAll('.product-gallery__slide').length > 1;

    this.splide = new window.Splide(this.sliderRoot, {
      type: hasMultipleMedia ? 'loop' : 'slide',
      start: initialSlide,
      rewind: hasMultipleMedia,
      perPage: 1,
      fixedWidth: hasMultipleMedia ? 302 : null,
      gap: hasMultipleMedia ? 16 : 0,
      padding: {
        left: 16,
        right: 16,
      },
      drag: hasMultipleMedia,
      arrows: false,
      pagination: hasMultipleMedia,
      keyboard: 'focused',
      mediaQuery: 'min',
      breakpoints: {
        768: {
          fixedWidth: hasMultipleMedia ? 600 : null,
        },
        991: {
          fixedWidth: null,
          gap: 0,
          padding: 0,
          pagination: false,
        },
      },
    });

    this.thumbnails.forEach((thumbnail) => {
      thumbnail.addEventListener('click', this.onThumbnailClick);
    });

    this.splide.on('move', this.setActiveThumbnail);
    this.splide.mount();
    this.setActiveThumbnail(this.splide.index);
  }

  disconnectedCallback() {
    this.thumbnails?.forEach((thumbnail) => {
      thumbnail.removeEventListener('click', this.onThumbnailClick);
    });
    this.splide?.destroy(true);
    this.splide = null;
  }

  onThumbnailClick(event) {
    const index = Number.parseInt(event.currentTarget.dataset.productGalleryThumbnail, 10);

    if (Number.isInteger(index)) {
      this.splide?.go(index);
    }
  }

  setActiveThumbnail(index) {
    this.thumbnails.forEach((thumbnail, thumbnailIndex) => {
      const isActive = thumbnailIndex === index;

      thumbnail.classList.toggle('is-active', isActive);
      thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }
}

if (!customElements.get('product-gallery')) {
  customElements.define('product-gallery', ProductGallery);
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();

    this.onButtonClick = this.onButtonClick.bind(this);
    this.onInputChange = this.onInputChange.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-quantity-input]');
    this.minusButton = this.querySelector('[data-quantity-minus]');
    this.plusButton = this.querySelector('[data-quantity-plus]');

    if (!this.input || !this.minusButton || !this.plusButton) return;

    this.minusButton.addEventListener('click', this.onButtonClick);
    this.plusButton.addEventListener('click', this.onButtonClick);
    this.input.addEventListener('change', this.onInputChange);
    this.updateButtons();
  }

  disconnectedCallback() {
    this.minusButton?.removeEventListener('click', this.onButtonClick);
    this.plusButton?.removeEventListener('click', this.onButtonClick);
    this.input?.removeEventListener('change', this.onInputChange);
  }

  onButtonClick(event) {
    const direction = event.currentTarget === this.plusButton ? 1 : -1;
    const value = (Number(this.input.value) || this.getMinimum()) + direction * this.getStep();

    this.setValue(value);
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  onInputChange() {
    this.setValue(Number(this.input.value) || this.getMinimum());
  }

  setValue(value) {
    const minimum = this.getMinimum();
    const step = this.getStep();
    const maximum = this.getMaximum();
    const increments = Math.max(0, Math.floor((value - minimum) / step));
    let constrainedValue = minimum + increments * step;

    if (maximum !== null) {
      const maximumIncrements = Math.max(0, Math.floor((maximum - minimum) / step));

      constrainedValue = Math.min(constrainedValue, minimum + maximumIncrements * step);
    }

    this.input.value = constrainedValue;
    this.updateButtons();
  }

  updateButtons() {
    const value = Number(this.input.value) || 0;
    const maximum = this.getMaximum();

    this.minusButton.disabled = value <= this.getMinimum();
    this.plusButton.disabled = maximum !== null && value + this.getStep() > maximum;
  }

  getMinimum() {
    return Number(this.input.min) || 1;
  }

  getStep() {
    return Number(this.input.step) || 1;
  }

  getMaximum() {
    return this.input.max === '' ? null : Number(this.input.max);
  }
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

class ProductVideos extends HTMLElement {
  constructor() {
    super();

    this.onClick = this.onClick.bind(this);
    this.onVideoIntersection = this.onVideoIntersection.bind(this);
  }

  connectedCallback() {
    this.videos = Array.from(this.querySelectorAll('[data-product-video]'));
    this.addEventListener('click', this.onClick);

    if (!('IntersectionObserver' in window) || this.videos.length === 0) return;

    this.observer = new IntersectionObserver(this.onVideoIntersection, {
      threshold: 0.25,
    });

    this.videos.forEach((video) => this.observer.observe(video));
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.observer?.disconnect();
  }

  onClick(event) {
    const slide = event.target.closest('.product-information__video-slide');

    if (!slide || !this.contains(slide)) return;

    const video = slide.querySelector('[data-product-video]');

    if (!video) return;

    event.preventDefault();

    if (video.paused) {
      this.playVideo(video);
    } else {
      this.pauseVideo(video);
    }
  }

  onVideoIntersection(entries) {
    entries.forEach((entry) => {
      if (!entry.isIntersecting && !entry.target.paused) {
        this.pauseVideo(entry.target);
      }
    });
  }

  playVideo(video) {
    this.videos.forEach((otherVideo) => {
      if (otherVideo !== video) {
        this.pauseVideo(otherVideo);
      }
    });

    video.muted = false;

    const playPromise = video.play();

    if (playPromise !== undefined) {
      playPromise
        .then(() => this.setVideoState(video, true))
        .catch(() => this.setVideoState(video, false));
      return;
    }

    this.setVideoState(video, true);
  }

  pauseVideo(video) {
    video.pause();
    this.setVideoState(video, false);
  }

  setVideoState(video, isPlaying) {
    const slide = video.closest('.product-information__video-slide');
    const playButton = slide?.querySelector('[data-product-video-play]');

    slide?.classList.toggle('is-playing', isPlaying);

    if (playButton) {
      playButton.hidden = isPlaying;
      playButton.setAttribute('aria-label', isPlaying ? 'Pause video' : 'Play video');
    }
  }
}

if (!customElements.get('product-videos')) {
  customElements.define('product-videos', ProductVideos);
}
